/**
 * Inicialización de componentes de MDB sobre markup nuevo
 * -----------------------------------------------------------------------------
 * `initMDB()` de MDB **no sirve para contenido dinámico**. Su registro de
 * componentes inicializados es de una sola vez:
 *
 *   if (!component || InitializedComponents.get(component.NAME)) return;
 *
 * Una vez que `Input` o `Select` pasaron por ahí, no vuelve a escanear el
 * documento nunca más. Todo lo que se cree después —un formulario generado, una
 * fila que llega por websocket, un modal con campos— se queda sin inicializar, y
 * volver a llamar a `initMDB()` no cambia nada.
 *
 * Aquí se instancian a mano, que es lo que hace MDB internamente la primera vez.
 * Es idempotente: los elementos que ya tienen instancia se saltan.
 */

import { selectES } from './locale-es.js';
import { normalizeText } from './normalize-text.js';

// Componentes que hay que construir por elemento.
//
// `Dropdown` está en la lista aunque MDB lo trate como «toggler»: su data-api
// delegado no crea la instancia al pulsar, así que un menú añadido después de
// arrancar no se abre. Comprobado con el selector de tamaño de página del
// paginador, que se genera cada vez que se repinta el listado.
const PER_ELEMENT = [
  { component: 'Input', selector: '[data-mdb-input-init]' },
  { component: 'Select', selector: '[data-mdb-select-init]', options: selectOptions },
  { component: 'Dropdown', selector: '[data-mdb-dropdown-init]' },
];

/**
 * Opciones con las que el theme arranca TODO desplegable.
 *
 * MDB trae el buscador apagado, y una lista de veinte municipios sin filtro se
 * recorre a mano. Aquí va encendido siempre; se apaga por elemento con
 * `data-mdb-filter="false"` cuando la lista es de dos opciones.
 *
 * El filtro propio busca sin tildes: el de MDB compara con `includes` a secas,
 * así que escribir «cafe» no encontraba «Café».
 */
function selectOptions(element) {
  // En los desplegables que consultan al servidor, quien filtra es el endpoint:
  // el criterio local dejaría fuera lo que el backend encontró por un campo que
  // no se ve en la etiqueta (un NIT, un código interno).
  const remote = element.hasAttribute('data-company-remote-url');

  const options = {
    filter: true,
    searchPlaceholder: selectES.searchPlaceholder,
    noResultText: selectES.noResultText,
    filterFn: remote
      ? () => true
      : (term, option) => normalizeText(option.label).includes(normalizeText(term)),
  };

  // Lo que venga en el HTML manda: MDB da prioridad al tercer argumento sobre
  // los `data-mdb-*`, así que las claves ya declaradas ahí no se tocan.
  Object.keys(options).forEach((key) => {
    const attribute = `data-mdb-${key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}`;
    if (element.hasAttribute(attribute)) delete options[key];
  });

  return options;
}

/**
 * @param {ParentNode} [root=document] Ámbito a recorrer.
 * @param {Object} components Componentes de MDB.
 */
export function initMdbOn(root = document, components = {}) {
  if (!root || typeof root.querySelectorAll !== 'function') return;

  PER_ELEMENT.forEach(({ component, selector, options }) => {
    const Component = components[component];
    if (!Component) return;

    root.querySelectorAll(selector).forEach((element) => {
      if (Component.getInstance(element)) return;

      // eslint-disable-next-line new-cap
      new Component(element, options ? options(element) : undefined);
    });
  });
}

export default initMdbOn;

/**
 * Vuelve a medir los campos de un ámbito que acaba de hacerse visible.
 *
 * MDB calcula el hueco del borde donde encaja el label con
 * `this._label.clientWidth * 0.8 + 8`. En un elemento oculto `clientWidth` es
 * CERO, así que la muesca queda en 8px y, cuando el label flota, el borde
 * superior le pasa por encima: se ve una línea tachando el texto.
 *
 * Pasa siempre que se inicializa algo que no está en pantalla —los pasos de un
 * formulario, una pestaña, el contenido de un modal—, y no se puede evitar
 * inicializando más tarde: el markup tiene que estar montado antes de mostrarse.
 * La salida es volver a medir al mostrarlo, que es lo que hace `update()`.
 *
 * @param {ParentNode} [root=document]
 * @param {Object} components Componentes de MDB.
 */
export function refreshMdbOn(root = document, components = {}) {
  const Input = components.Input;
  if (!Input || !root || typeof root.querySelectorAll !== 'function') return;

  root.querySelectorAll('[data-mdb-input-init]').forEach((element) => {
    const instance = Input.getInstance(element);
    if (instance) instance.update();
  });
}
